import {toast} from 'sonner';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {VERIFICATION_METHODS, VerificationMethod} from '@guestapp/sdk';
import {useChekinSDK} from 'context/ChekinSDK/useChekinSDK';
import {useMockPromise} from 'hooks/useMockPromise';
import {PATTERNS} from '@guestapp/core';
import {useReservation} from 'context/reservation';

type Props = {
  type: VerificationMethod;
  onGenerate?: () => void;
  onVerify?: (data: string, isVerified: boolean) => void;
  isMock?: boolean;
};

const validateTarget = (data: string, method: VerificationMethod) => {
  if (method === VERIFICATION_METHODS.email) {
    const emailRegex = PATTERNS.email;
    return emailRegex.test(data);
  }
  if (method === VERIFICATION_METHODS.phone) {
    const phoneRegex = PATTERNS.phone;
    return phoneRegex.test(data);
  }
  return false;
};

const useVerification = ({
  type,
  onGenerate = () => {},
  onVerify = () => {},
  isMock = false,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [contactsData, setContactsData] = useState<string>('');
  const {data: reservation} = useReservation();
  const {t} = useTranslation();
  const mockPromise = useMockPromise();

  const {generate, verify} = useChekinSDK();

  const onSubmit = async (data: string) => {
    setIsLoading(true);
    try {
      if (!data || !reservation?.id) return;
      if (!validateTarget(data, type)) {
        throw new Error(
          type === VERIFICATION_METHODS.email ? 'invalid_email' : 'invalid_phone_number',
        );
      }
      await (isMock
        ? mockPromise()
        : generate({method: type, target: data.trim(), reservation: reservation.id}));

      setContactsData(data);
      onGenerate();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code: string) => {
    setIsLoading(true);
    try {
      if (!reservation?.id) return;
      const result = await (isMock
        ? mockPromise()
        : verify({
            method: type,
            target: String(contactsData.trim()),
            credential: code,
            reservation: reservation.id,
          }).then(r => r.verified));

      if (!result) {
        throw new Error('verification_failed');
      }
      onVerify?.(contactsData, !!(contactsData && code));
    } catch {
      toast.error(t('invalid_verification_code'));
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    setIsLoading(true);
    try {
      if (!reservation?.id) return;
      await (isMock
        ? mockPromise()
        : generate({
            method: type,
            target: String(contactsData.trim()),
            reservation: reservation.id,
          }));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    contactsData,
    onSubmit,
    verifyCode,
    resendCode,
  };
};

export {useVerification};
